import { devtools } from 'zustand/middleware'
import { UseBoundStoreWithEqualityFn, createWithEqualityFn } from 'zustand/traditional'

import { StoreApi } from 'zustand'
import { deepEqual } from './deep-equal'
import {
  taskDisplayName,
  sameTaskArguments,
  sameTaskSlot,
  type RuntimeTaskDeclaration,
  taskProgramStream,
  type TaskProgram
} from './task'
import {
  AppState,
  EffectOutput,
  UpdateResult,
  InitArgOf,
  MessageOf,
  normalizeEffectOutput,
  AnyModule,
  ProcessHandle,
  ProcessMap,
  ProcessMessage,
  ProcessName
} from './process'
import {
  Cause,
  Clock,
  Context,
  Deferred,
  Effect,
  Exit,
  Fiber,
  FiberSet,
  Layer,
  Pull,
  Result,
  Scope,
  Stream
} from 'effect'
import { RuntimeDefectReporter } from './runtime-defect-reporter'
import { assertReplayable } from './replayable'

type StoreEvent = { type: string; [key: string]: unknown }

type TimedStoreEvent = StoreEvent & { time: string }

type DispatchEvent<State, Message> = (event: Message, context: EventContext<State>) => void

type EventContext<State> = {
  kind: 'init' | 'event'
  time: string
  prevState: State
  nextState: State
}

type TimelineOriginKind = 'init' | 'dispatch' | 'task' | 'background' | 'run'

type TimelineSpanEvent = {
  name: string
  time: string
  attributes: Record<string, unknown>
}

type TimelineSpanSnapshot = {
  name: string
  spanId?: string
  traceId?: string
  attributes: Record<string, unknown>
  events: TimelineSpanEvent[]
  eventsTruncated: boolean
}

type TimelineBaseRecord = {
  id: string
  time: string
  storeName: string
  processName: string
}

export type TimelineMessageRecord = TimelineBaseRecord & {
  kind: 'message'
  dispatchId: string
  parentEffectId?: string
  message: StoreEvent
}

export type TimelineEffectStartRecord = TimelineBaseRecord & {
  kind: 'effectStart'
  effectId: string
  originKind: TimelineOriginKind
  triggerDispatchId?: string
  parentEffectId?: string
  taskKey?: string
  spanName: string
}

export type TimelineEffectEndRecord = TimelineBaseRecord & {
  kind: 'effectEnd'
  effectId: string
  status: 'resolved' | 'failed' | 'interrupted'
  emittedMessageTypes: string[]
  span: TimelineSpanSnapshot
}

export type TimelineTaskChangeRecord = TimelineBaseRecord & {
  kind: 'taskChange'
  taskKey: string
  action: 'started' | 'completed' | 'stopped'
  triggerDispatchId?: string
}

export type TimelineRecord =
  | TimelineMessageRecord
  | TimelineEffectStartRecord
  | TimelineEffectEndRecord
  | TimelineTaskChangeRecord

export type Timeline = {
  records(): readonly TimelineRecord[]
  messages(): readonly StoreEvent[]
  subscribe(listener: (record: TimelineRecord) => void): () => void
}

type Process = {
  name: string
  module: AnyModule
  initArg: any
  lease: ProcessLease
  handle: ProcessHandle<ProcessMessage>
  tasks: ActiveTask[]
  closingTasks: ClosingTask[]
  desiredTasks: ReadonlyArray<RuntimeTaskDeclaration>
  desiredTasksTriggerDispatchId?: string
}

type ProcessLease = {
  active: boolean
}

type TaskToken = {
  active: boolean
}

/*
 * The reconciler has one active or closing session per structured
 * (scope, definition name) slot. Definition identity must remain stable while
 * arguments choose the session within that slot. Revocation flips the token
 * before interruption, so late sends are inert. A closing slot prevents its
 * replacement from starting until finalizers finish; the latest desired
 * declaration wins if state changes again while closing. Completed sessions
 * remain latched until their declaration disappears or its arguments change.
 * Sessions that fail or die are reported and latch the same way, preventing an
 * implicit retry loop.
 * Reconciliation follows the state commit and deliberately has no rollback:
 * duplicate slots and unstable definitions are programmer errors that crash
 * at that boundary.
 */
type ActiveTask = {
  declaration: RuntimeTaskDeclaration
  token: TaskToken
  status: 'running' | 'completed'
  stop: Effect.Effect<void>
}

type ClosingTask = {
  declaration: RuntimeTaskDeclaration
}

type ActiveUpdateEffect = {
  stop: Effect.Effect<void>
}

type DisposalState =
  | { status: 'running'; completed: Deferred.Deferred<void> }
  | { status: 'complete'; exit: Exit.Exit<void> }

type SendOpts = {
  from?: ProcessName
  parentEffectId?: string
  taskToken?: TaskToken
  processLease?: ProcessLease
}

type ReduxStoreConstructor = {
  name: string
  onEvent?: DispatchEvent<any, any>
  onTimelineRecord?: (record: TimelineRecord) => void
  processes?: Partial<ProcessMap>
  initArgs?: Partial<Record<string, unknown>>
  onError?: (error: any) => void
  effectLayer: Layer.Layer<never, any, RuntimeDefectReporter>
  /** Timeline records are replayable, so traced state and messages are validated as plain data. */
  enableTimeline?: boolean
  /** Host capability decided at the application root; the store never reads browser globals. */
  enableDevtools?: boolean
}

type PendingBootEffect = {
  processName: string
  messageType?: string
  parentEffectId?: string
  triggerDispatchId?: string
  program: TaskProgram<EffectOutput<ProcessMessage>, unknown, unknown>
}

type EffectExecutionContext = {
  effectId: string
  processName: string
  originKind: TimelineOriginKind
  spanName: string
  triggerDispatchId?: string
  parentEffectId?: string
  taskKey?: string
}

type NativeSpanLike = {
  name: string
  spanId?: string
  traceId?: string
  attributes: ReadonlyMap<string, unknown>
  status: {
    _tag: 'Started' | 'Ended'
    startTime: bigint
    endTime?: bigint
  }
  events?: Array<[name: string, startTime: bigint, attributes: Record<string, unknown>]>
}

type ProcessInitArgs<P extends ProcessMap> = Partial<{
  [K in keyof P]: InitArgOf<P[K]>
}>

type ReduxStoreConfig<P extends ProcessMap> = Omit<
  ReduxStoreConstructor,
  'processes' | 'initArgs'
> & {
  processes?: Partial<P>
  initArgs?: ProcessInitArgs<P>
}

type StoreExecution = {
  readonly context: Context.Context<any>
  readonly clock: Clock.Clock
  readonly fibers: FiberSet.FiberSet<unknown, unknown>
  readonly fork: <A, E>(effect: Effect.Effect<A, E>) => Fiber.Fiber<A, E>
  readonly defect: Deferred.Deferred<never, unknown>
}

function effectExitStatus(exit: Exit.Exit<unknown, unknown>): TimelineEffectEndRecord['status'] {
  if (Exit.isSuccess(exit)) {
    return 'resolved'
  }
  if (Result.isSuccess(Pull.filterDone(exit.cause))) {
    // A stopped Stream can report Done together with its interruption. Done is
    // not a failure, but the timeline still records that lifecycle as stopped.
    if (Cause.hasInterrupts(exit.cause)) {
      return 'interrupted'
    }
    return 'resolved'
  }
  if (Cause.hasInterruptsOnly(exit.cause)) {
    return 'interrupted'
  }
  return 'failed'
}

/**
 * Builds one store inside the caller's Effect scope.
 *
 * The store does not create or own a runtime. Its Layers are built into the
 * caller's Scope, and synchronous sends fork Commands and Tasks into the
 * captured runtime through one scoped FiberSet. Closing the caller's Scope or
 * disposing the store interrupts every owned fiber and releases every Layer.
 */
export function makeReduxStore<P extends ProcessMap = ProcessMap>(
  config: ReduxStoreConfig<P>
): Effect.Effect<ReduxStore<P>, any, Scope.Scope> {
  return Effect.gen(function* () {
    const scope = yield* Effect.scope
    const defect = yield* Deferred.make<never, unknown>()
    const processEntries = Object.entries(config.processes ?? {}) as Array<
      [keyof P & string, P[keyof P & string]]
    >
    const moduleLayers = processEntries
      .map(([, module]) => module.layer)
      .filter(
        (layer): layer is Layer.Layer<never, any, RuntimeDefectReporter> => layer != undefined
      )
    const reportError =
      typeof config.onError == 'function'
        ? config.onError
        : (error: unknown) => console.error(error)
    const recordDetachedDefect = (error: unknown) => {
      Deferred.doneUnsafe(defect, Effect.fail(error))
    }
    const defectReporterLayer = Layer.succeed(RuntimeDefectReporter, {
      report: (context: string, error: unknown) =>
        Effect.sync(() => {
          console.error(`[detached effect failure] ${context}`, error)
          recordDetachedDefect(error)
          reportError(error)
        })
    })
    const appLayer = Layer.mergeAll(config.effectLayer, ...moduleLayers).pipe(
      Layer.provide(defectReporterLayer)
    )
    const layerContext = yield* Layer.buildWithScope(
      Layer.merge(defectReporterLayer, appLayer),
      scope
    )
    return yield* Effect.gen(function* () {
      const clock = yield* Clock.clockWith(Effect.succeed)
      const fibers = yield* FiberSet.make<unknown, unknown>()
      const fork = yield* FiberSet.runtime(fibers)<never>()
      const store = new ReduxStore(config, {
        context: layerContext,
        clock,
        fibers,
        fork,
        defect
      })
      yield* Effect.addFinalizer(() => store.finalizeScope())
      return store
    }).pipe(Effect.provide(layerContext))
  })
}

/**
 * Runs modules as one Elm-style store over an Effect runtime.
 *
 * Messages are reduced serially. Each reduction commits state, reconciles
 * state-owned Tasks, then starts transition-owned Commands. Task declarations
 * are desired state rather than instructions: the reconciler retains matching
 * sessions, revokes obsolete sessions before interruption, and waits for their
 * finalizers before reusing a slot.
 */
export class ReduxStore<P extends ProcessMap = ProcessMap> {
  private readonly HISTORY_SIZE = 100
  private readonly TIMELINE_SIZE = 500
  private readonly TIMELINE_EVENT_LIMIT = 50

  private storeName: string
  private useAppStore: UseBoundStoreWithEqualityFn<StoreApi<AppState<P>>>
  private disposing = false
  private enableTimeline: boolean
  private onTimelineRecord?: (record: TimelineRecord) => void
  private eventHistory: TimedStoreEvent[] = []
  private timelineHistory: TimelineRecord[] = []
  private timelineListeners = new Set<(record: TimelineRecord) => void>()
  private timelineRecordSeq = 0
  private disposal: DisposalState | undefined
  private dispatchSeq = 0
  private effectSeq = 0
  // Store messages are TEA-style: one message is processed to completion before the next starts.
  private sendQueue: Array<[string, ProcessMessage, SendOpts]> = []
  private isDrainingSendQueue = false
  // Commands outlive state transitions, so their fibers are owned by the store.
  private activeUpdateEffects = new Set<ActiveUpdateEffect>()

  /** Live module instance and Task-reconciliation state, keyed by process name. */
  private processes: Record<ProcessName, Process> = {}

  private subscribeListeners: DispatchEvent<any, StoreEvent>[] = []

  private onError: (error: any) => void
  private readonly execution: StoreExecution
  private _reportError = (error: any) => {
    if (typeof this.onError == 'function') {
      this.onError(error)
    } else {
      console.error(error)
    }
  }

  private _recordDetachedDefect = (error: unknown) => {
    Deferred.doneUnsafe(this.execution.defect, Effect.fail(error))
  }

  constructor(
    {
      name,
      onEvent,
      onTimelineRecord,
      onError,
      processes = {},
      initArgs = {},
      enableTimeline = false,
      enableDevtools = false
    }: ReduxStoreConfig<P>,
    execution: StoreExecution
  ) {
    this.execution = execution
    this.storeName = name
    this.enableTimeline = enableTimeline || onTimelineRecord !== undefined
    this.onTimelineRecord = onTimelineRecord

    this.useAppStore = createWithEqualityFn<AppState<P>>()(
      devtools(() => ({}) as AppState<P>, {
        name: name,
        store: name,
        enabled: enableDevtools
      }),
      deepEqual
    )

    this.onError =
      typeof onError == 'function'
        ? onError
        : (error) => {
            console.error(error)
          }
    const processEntries = Object.entries(processes) as Array<
      [keyof P & string, P[keyof P & string]]
    >

    if (onEvent) {
      this.subscribe(onEvent)
    }

    for (const [processName, module] of processEntries) {
      this.registerProcess(processName, module, initArgs[processName])
    }
    if (processEntries.length > 0) {
      const { prevStoreState, nextStoreState, pendingBootEffects } = this._bootAllProcesses()
      const initEvent = { type: 'INIT' }
      const time = this._nowIso()
      const dispatchId = this._nextDispatchId()
      if (this.enableTimeline) {
        assertReplayable(nextStoreState, '$state')
      }
      ;(this.useAppStore.setState as any)(nextStoreState, true, initEvent)
      this._publishEvent(
        initEvent,
        {
          kind: 'init',
          prevState: prevStoreState,
          nextState: nextStoreState,
          time
        },
        {
          processName: 'store',
          dispatchId
        }
      )

      this.isDrainingSendQueue = true
      this._updateAllTasks(this.useAppStore.getState(), dispatchId)
      this.isDrainingSendQueue = false
      this._drainSendQueue()
      for (const pendingBootEffect of pendingBootEffects) {
        // Defer init output one scheduler turn so `runFork` cannot re-enter
        // dispatch while the constructor is active. Every init Command is
        // forked before any output is reduced.
        this._runUpdateEffect(
          pendingBootEffect.processName,
          Stream.fromEffect(Effect.yieldNow).pipe(
            Stream.flatMap(() => taskProgramStream(pendingBootEffect.program))
          ),
          pendingBootEffect.messageType,
          pendingBootEffect.triggerDispatchId,
          pendingBootEffect.parentEffectId
        )
      }
    }
  }

  /**
   * Raw store state access for tests and tooling.
   *
   * App code should prefer selectors and derived reads over direct store access.
   */
  getState = (): AppState<P> => this.useAppStore.getState()

  /**
   * Returns the view-facing handle for the current process instance.
   *
   * The handle is stable for that instance and becomes inert when the process
   * is removed or replaced. Direct `send` remains strict for runtime callers.
   */
  getProcessHandle = <K extends keyof P & string>(
    processName: K
  ): ProcessHandle<MessageOf<P[K]>> => {
    return this._resolveProcess(processName).handle as ProcessHandle<MessageOf<P[K]>>
  }

  getTimeline = (): Timeline => ({
    records: () => this.timelineHistory.slice(),
    messages: () =>
      this.enableTimeline
        ? this.timelineHistory
            .filter((record): record is TimelineMessageRecord => record.kind == 'message')
            .map((record) => record.message)
        : this.eventHistory.map(({ time: _time, ...message }) => message),
    subscribe: (listener) => {
      this.timelineListeners.add(listener)
      return () => {
        this.timelineListeners.delete(listener)
      }
    }
  })

  private _resolveProcess = (processName: string, message?: any) => {
    const process = this.processes[processName]
    if (!process) {
      throw new ProcessMissingException({ processName, message })
    }

    return process
  }

  private registerProcess = (processName: string, module: AnyModule, initArg?: any) => {
    if (processName in this.processes) {
      throw new Error(`process ${processName} already running`)
    }

    const lease: ProcessLease = { active: true }
    const handle: ProcessHandle<ProcessMessage> = {
      send: (messages) => {
        if (!lease.active || this.processes[processName]?.lease !== lease) {
          return
        }

        this.send(processName, messages, { processLease: lease })
      }
    }

    this.processes[processName] = {
      name: processName,
      module: module as AnyModule,
      initArg: initArg,
      lease,
      handle,
      tasks: [],
      closingTasks: [],
      desiredTasks: []
    }
  }

  private _bootAllProcesses = (): {
    prevStoreState: any
    nextStoreState: any
    pendingBootEffects: PendingBootEffect[]
  } => {
    const prevStoreState = this.useAppStore.getState()
    const nextStoreState = { ...prevStoreState }
    const pendingBootEffects: PendingBootEffect[] = []

    for (const process of Object.values(this.processes)) {
      const { nextProcessState, updateEffect } = callMessageHandler(
        process,
        { type: 'init' },
        (nextStoreState as any)[process.name]
      )

      if (nextProcessState == undefined) {
        delete (nextStoreState as any)[process.name]
      } else {
        ;(nextStoreState as any)[process.name] = nextProcessState
      }

      if (!this.disposing && updateEffect) {
        pendingBootEffects.push({
          processName: process.name,
          messageType: 'init',
          program: updateEffect
        })
      }
    }

    return { prevStoreState, nextStoreState, pendingBootEffects }
  }

  private disposeProcess = (processName: string) => {
    const process = this._resolveProcess(processName)
    process.lease.active = false

    this.send(process.name, { type: `dispose` })

    delete this.processes[process.name]
  }

  private _startTask = (
    process: Process,
    declaration: RuntimeTaskDeclaration,
    triggerDispatchId?: string
  ) => {
    const taskKey = taskDisplayName(declaration)
    const context = this._createEffectContext({
      originKind: 'task',
      processName: process.name,
      taskKey,
      triggerDispatchId
    })
    const token: TaskToken = { active: true }
    const started = Deferred.makeUnsafe<Effect.Effect<void>>()
    const active: ActiveTask = {
      declaration,
      token,
      status: 'running',
      stop: Deferred.await(started).pipe(Effect.flatten)
    }
    let completionRecorded = false

    const complete = () => {
      // "completed" is a terminal latch, not a success claim. `ensuring` runs
      // after success, typed failure, defect, or interruption. An unchanged
      // declaration stays latched; removal or new arguments closes it first.
      active.status = 'completed'
      if (!token.active || completionRecorded) {
        return
      }

      completionRecorded = true
      this._recordTaskChange(process.name, taskKey, 'completed', triggerDispatchId)
    }

    const send = (message: EffectOutput<ProcessMessage>) => {
      if (!token.active) {
        return
      }

      const nextMessages = normalizeEffectOutput(message)
      if (nextMessages.length == 0) {
        return
      }

      this.send(process.name, [...nextMessages], {
        from: process.name,
        parentEffectId: context.effectId,
        taskToken: token,
        processLease: process.lease
      })
    }

    process.tasks.push(active)
    this._recordTaskChange(process.name, taskKey, 'started', triggerDispatchId)

    const emittedMessageTypes: string[] = []
    const run = Stream.runForEach(taskProgramStream(declaration.program()), (output) =>
      Effect.sync(() => {
        emittedMessageTypes.push(...normalizeEffectOutput(output).map((message) => message.type))
        send(output)
      })
    )
    const managedEffect = run.pipe(Effect.ensuring(Effect.sync(complete)), Effect.asVoid)
    Deferred.doneUnsafe(
      started,
      Effect.succeed(this._forkEffect(context, managedEffect, () => emittedMessageTypes))
    )
  }

  private _closeTask = (process: Process, active: ActiveTask, triggerDispatchId?: string) => {
    active.token.active = false
    process.tasks.splice(process.tasks.indexOf(active), 1)

    const taskKey = taskDisplayName(active.declaration)
    this._recordTaskChange(process.name, taskKey, 'stopped', triggerDispatchId)

    const closing: ClosingTask = {
      declaration: active.declaration
    }
    process.closingTasks.push(closing)
    const finishClosing = () => {
      const closingIndex = process.closingTasks.indexOf(closing)
      if (closingIndex >= 0) {
        process.closingTasks.splice(closingIndex, 1)
      }

      if (!this.disposing && this.processes[process.name] == process) {
        // State may change repeatedly while the old session finalizes. Start
        // only the latest declaration desired for this slot.
        const desired = process.desiredTasks.find((task) => sameTaskSlot(task, closing.declaration))
        const alreadyRunning = process.tasks.some((task) =>
          sameTaskSlot(task.declaration, closing.declaration)
        )
        if (desired && !alreadyRunning) {
          this._startTask(process, desired, process.desiredTasksTriggerDispatchId)
        }
      }
    }

    const stopFiber = this._forkManagedFiber(
      active.stop.pipe(Effect.ensuring(Effect.sync(finishClosing)))
    )
    return Fiber.await(stopFiber).pipe(Effect.asVoid)
  }

  private _stopAllTasks = (process: Process, triggerDispatchId?: string) => {
    process.desiredTasks = []
    process.desiredTasksTriggerDispatchId = triggerDispatchId
    return [...process.tasks].map((task) => this._closeTask(process, task, triggerDispatchId))
  }

  private _updateTasks = (process: Process, nextProcessState: any, triggerDispatchId?: string) => {
    const nextTasks =
      nextProcessState == undefined ? [] : process.module.tasks?.(nextProcessState) ?? []

    for (let left = 0; left < nextTasks.length; left++) {
      for (let right = left + 1; right < nextTasks.length; right++) {
        if (sameTaskSlot(nextTasks[left], nextTasks[right])) {
          throw new Error(
            `Duplicate Task declaration process=${process.name} task=${taskDisplayName(
              nextTasks[left]
            )}`
          )
        }
      }
    }

    // Publish desired state before starting any close. `finishClosing` resolves
    // asynchronously and consults `desiredTasks` then; it must observe this
    // reconciliation's declarations, never the previous ones.
    process.desiredTasks = nextTasks
    process.desiredTasksTriggerDispatchId = triggerDispatchId

    for (const active of [...process.tasks]) {
      const desired = nextTasks.find((task) => sameTaskSlot(active.declaration, task))
      if (!desired || !sameTaskArguments(active.declaration.arguments, desired.arguments)) {
        this._closeTask(process, active, triggerDispatchId)
      }
    }

    for (const desired of nextTasks) {
      const active = process.tasks.some((task) => sameTaskSlot(task.declaration, desired))
      const closing = process.closingTasks.some((task) => sameTaskSlot(task.declaration, desired))
      if (!active && !closing) {
        this._startTask(process, desired, triggerDispatchId)
      }
    }
  }

  private _updateAllTasks = (nextStoreState: any, triggerDispatchId?: string) => {
    for (const process of Object.values(this.processes)) {
      this._updateTasks(process, nextStoreState[process.name], triggerDispatchId)
    }
  }

  send<K extends keyof P & string>(
    processName: K,
    message: MessageOf<P[K]> | MessageOf<P[K]>[],
    opts?: SendOpts
  ): void

  send<E extends ProcessMessage>(processName: string, message: E | E[], opts?: SendOpts): void

  send(processName: string, message: ProcessMessage | ProcessMessage[], opts: SendOpts = {}) {
    if (opts.taskToken && !opts.taskToken.active) {
      return
    }
    if (
      opts.processLease &&
      (!opts.processLease.active || this.processes[processName]?.lease !== opts.processLease)
    ) {
      return
    }

    this._resolveProcess(processName, message)

    const messages = Array.isArray(message) ? message : [message]
    for (const msg of messages) {
      this.sendQueue.push([processName, msg, opts])
    }

    this._drainSendQueue()
  }

  private _drainSendQueue = () => {
    if (this.isDrainingSendQueue) {
      return
    }

    this.isDrainingSendQueue = true
    try {
      // Nested sends from subscriptions/effects append here instead of re-entering dispatch.
      while (this.sendQueue.length > 0) {
        const [processName, msg, opts] = this.sendQueue.shift()!
        // Recheck at dispatch, not only enqueue: a Task forked mid-drain can
        // enqueue output that a still-queued message then revokes.
        if (opts.taskToken && !opts.taskToken.active) {
          continue
        }
        if (
          opts.processLease &&
          (!opts.processLease.active || this.processes[processName]?.lease !== opts.processLease)
        ) {
          continue
        }

        const process = this._resolveProcess(processName, msg)
        if (this.enableTimeline) {
          assertReplayable(msg, `$message.${processName}`)
        }
        const time = this._nowIso()
        const dispatchId = this._nextDispatchId()
        const curStoreState = this.useAppStore.getState()
        const curProcessState = (curStoreState as any)[processName]

        const { nextProcessState, updateEffect } = callMessageHandler(process, msg, curProcessState)

        const nextStoreState = {
          ...this.useAppStore.getState(),
          [processName]: nextProcessState
        }
        // undefined is equivalent to saying delete the state
        if (nextProcessState === undefined) {
          delete nextStoreState[processName]
        }
        if (this.enableTimeline) {
          assertReplayable(nextProcessState, `$state.${processName}`)
        }
        const namespacedMessage: any = {
          ...msg,
          type: `${processName}/${msg.type}`
        }
        if (opts.from) {
          namespacedMessage.from = opts.from
        }
        const inspectionMessage = redactForInspection(namespacedMessage) as StoreEvent
        ;(this.useAppStore.setState as any)(
          nextStoreState,
          true,
          // we namespace the message so it shows up in redux like for ex "processName/msg" router/updated
          inspectionMessage
        )

        this._publishEvent(
          namespacedMessage as StoreEvent,
          {
            kind: 'event',
            prevState: curStoreState,
            nextState: nextStoreState,
            time
          },
          {
            processName,
            dispatchId,
            parentEffectId: opts.parentEffectId
          }
        )

        if (!this.disposing) {
          this._updateAllTasks(nextStoreState, dispatchId)
        }

        if (!this.disposing && updateEffect) {
          this._runUpdateEffect(
            processName,
            updateEffect,
            msg.type,
            dispatchId,
            opts.parentEffectId
          )
        }
      }
    } finally {
      this.isDrainingSendQueue = false
    }
  }

  private _runUpdateEffect = (
    processName: string,
    updateEffect: TaskProgram<EffectOutput<ProcessMessage>, unknown, unknown>,
    messageType?: string,
    triggerDispatchId?: string,
    parentEffectId?: string
  ) => {
    const context = this._createEffectContext({
      originKind: messageType == 'init' ? 'init' : 'dispatch',
      processName,
      messageType,
      triggerDispatchId,
      parentEffectId
    })
    const emittedMessageTypes: string[] = []
    const run = Stream.runForEach(taskProgramStream(updateEffect), (output) => {
      emittedMessageTypes.push(...normalizeEffectOutput(output).map((message) => message.type))
      return this._dispatchCommandMessages(processName, context.effectId, output)
    })
    const managedEffect = run.pipe(
      Effect.ensuring(
        Effect.sync(() => {
          this.activeUpdateEffects.delete(activeUpdateEffect)
        })
      ),
      Effect.asVoid
    )
    const started = Deferred.makeUnsafe<Effect.Effect<void>>()
    const activeUpdateEffect: ActiveUpdateEffect = {
      stop: Deferred.await(started).pipe(Effect.flatten)
    }
    // `runFork` may finish synchronously. Register ownership first so the
    // `ensuring` cleanup can always remove the same entry.
    this.activeUpdateEffects.add(activeUpdateEffect)
    Deferred.doneUnsafe(
      started,
      Effect.succeed(this._forkEffect(context, managedEffect, () => emittedMessageTypes))
    )
  }

  private _forkEffect = <A, E>(
    context: EffectExecutionContext,
    effect: Effect.Effect<A, E, unknown>,
    resolveEmittedMessageTypes?: () => string[]
  ) => {
    const managedEffect = Effect.scoped(effect as Effect.Effect<A, E, never>)
    const fiber = this.execution.fork(
      this._wrapEffect(context, managedEffect, resolveEmittedMessageTypes)
    )
    return Fiber.interrupt(fiber).pipe(Effect.asVoid)
  }

  forkBackgroundEffect = <A, E>(
    label: string,
    effect: Effect.Effect<A, E, unknown>
  ): Effect.Effect<void> =>
    this._forkEffect(
      this._createEffectContext({
        originKind: 'background',
        processName: `store:${this.storeName}:${label}`,
        label
      }),
      effect
    )

  run = <A, E, R>(effect: Effect.Effect<A, E, R>): Effect.Effect<A, E> =>
    this._wrapEffect(
      this._createEffectContext({
        originKind: 'run',
        processName: `store:${this.storeName}`,
        label: 'run'
      }),
      effect as Effect.Effect<A, E, unknown>
    ).pipe(Effect.provide(this.execution.context))

  /**
   * Fails with the first unexpected failure from store-owned detached work.
   * Hosts may race this with their own lifetime so a broken Command, Task, or
   * background service cannot disappear inside its forked fiber.
   */
  awaitDefect = (): Effect.Effect<never, unknown> => Deferred.await(this.execution.defect)

  private _dispatchCommandMessages = (
    processName: string,
    effectId: string,
    effectMessage: EffectOutput<ProcessMessage> | null
  ) =>
    Effect.sync(() => {
      if (this.disposing) {
        return
      }

      const nextMessages = normalizeEffectOutput(effectMessage)
      if (nextMessages.length == 0) {
        return
      }

      this.send(processName, [...nextMessages], {
        from: processName,
        parentEffectId: effectId
      })
    })

  private _forkManagedFiber = <A, E>(effect: Effect.Effect<A, E, unknown>) => {
    // Forked module work erases its Requirements at this boundary. The cast
    // trusts that the application and module layers provide every service;
    // nothing proves it. A missing service surfaces as a runtime defect.
    return this.execution.fork(Effect.scoped(effect as Effect.Effect<A, E, never>))
  }

  /**
   * The single observation and failure boundary for work owned by the store.
   *
   * With timeline capture enabled, every invocation records one start and one
   * terminal entry under the same span. Non-interruption causes are reported
   * here and keep their original failure semantics. Fork ownership isolates
   * Commands and Tasks; direct `run` callers observe rejection.
   */
  private _wrapEffect = <A, E, R>(
    context: EffectExecutionContext,
    effect: Effect.Effect<A, E, R>,
    resolveEmittedMessageTypes?: () => string[]
  ): Effect.Effect<A, E, R> => {
    const attributes: Record<string, unknown> = {
      'redux.storeName': this.storeName,
      'redux.processName': context.processName,
      'redux.effectId': context.effectId,
      'redux.originKind': context.originKind
    }

    if (context.triggerDispatchId) {
      attributes['redux.triggerDispatchId'] = context.triggerDispatchId
    }
    if (context.parentEffectId) {
      attributes['redux.parentEffectId'] = context.parentEffectId
    }
    if (context.taskKey) {
      attributes['redux.taskKey'] = context.taskKey
    }

    this._appendTimeline({
      kind: 'effectStart',
      id: this._nextTimelineRecordId(),
      time: this._nowIso(),
      storeName: this.storeName,
      processName: context.processName,
      effectId: context.effectId,
      originKind: context.originKind,
      triggerDispatchId: context.triggerDispatchId,
      parentEffectId: context.parentEffectId,
      taskKey: context.taskKey,
      spanName: context.spanName
    })

    return Effect.useSpan(context.spanName, (span) => {
      return Effect.gen(function* () {
        yield* Effect.annotateCurrentSpan(attributes)
        return yield* effect
      }).pipe(
        Effect.withParentSpan(span),
        Effect.onExit((exit) =>
          Effect.sync(() => {
            const status = effectExitStatus(exit)

            if (!Exit.isSuccess(exit) && status == 'failed') {
              const error = Cause.squash(exit.cause)
              console.error(`[effect failure] ${context.processName}\n${Cause.pretty(exit.cause)}`)
              if (context.originKind != 'run') {
                this._recordDetachedDefect(error)
              }
              this._reportError(error)
            }

            this._appendTimeline({
              kind: 'effectEnd',
              id: this._nextTimelineRecordId(),
              time: this._nowIso(),
              storeName: this.storeName,
              processName: context.processName,
              effectId: context.effectId,
              status,
              emittedMessageTypes:
                Exit.isSuccess(exit) && resolveEmittedMessageTypes
                  ? resolveEmittedMessageTypes()
                  : [],
              span: this._snapshotSpan(span as NativeSpanLike, context.spanName)
            })
          }).pipe(Effect.withParentSpan(span))
        )
      )
    })
  }

  private _createEffectContext(args: {
    originKind: TimelineOriginKind
    processName: string
    messageType?: string
    triggerDispatchId?: string
    parentEffectId?: string
    taskKey?: string
    label?: string
  }): EffectExecutionContext {
    const effectId = this._nextEffectId()

    switch (args.originKind) {
      case 'init':
        return {
          effectId,
          processName: args.processName,
          originKind: 'init',
          spanName: `${args.processName}/init`,
          triggerDispatchId: args.triggerDispatchId,
          parentEffectId: args.parentEffectId
        }
      case 'dispatch':
        return {
          effectId,
          processName: args.processName,
          originKind: 'dispatch',
          spanName: `${args.processName}/${args.messageType ?? '<unknown>'}`,
          triggerDispatchId: args.triggerDispatchId,
          parentEffectId: args.parentEffectId
        }
      case 'task':
        return {
          effectId,
          processName: args.processName,
          originKind: 'task',
          spanName: `${args.processName}/task:${args.taskKey}`,
          triggerDispatchId: args.triggerDispatchId,
          parentEffectId: args.parentEffectId,
          taskKey: args.taskKey
        }
      case 'background':
        return {
          effectId,
          processName: args.processName,
          originKind: 'background',
          spanName: `store:${this.storeName}/background:${args.label ?? args.processName}`,
          triggerDispatchId: args.triggerDispatchId,
          parentEffectId: args.parentEffectId
        }
      case 'run':
        return {
          effectId,
          processName: args.processName,
          originKind: 'run',
          spanName: `store:${this.storeName}/run`,
          triggerDispatchId: args.triggerDispatchId,
          parentEffectId: args.parentEffectId
        }
    }

    throw new Error(`Unknown effect origin kind: ${args.originKind}`)
  }

  private _recordTaskChange = (
    processName: string,
    taskKey: string,
    action: 'started' | 'completed' | 'stopped',
    triggerDispatchId?: string
  ) => {
    this._appendTimeline({
      kind: 'taskChange',
      id: this._nextTimelineRecordId(),
      time: this._nowIso(),
      storeName: this.storeName,
      processName,
      taskKey,
      action,
      triggerDispatchId
    })
  }

  private _publishEvent = (
    event: StoreEvent,
    context: EventContext<any>,
    meta: { processName: string; dispatchId: string; parentEffectId?: string }
  ) => {
    const inspectionEvent = redactForInspection(event) as StoreEvent
    if (this.enableTimeline) {
      this._appendTimeline({
        kind: 'message',
        id: this._nextTimelineRecordId(),
        time: context.time,
        storeName: this.storeName,
        processName: meta.processName,
        dispatchId: meta.dispatchId,
        parentEffectId: meta.parentEffectId,
        message: inspectionEvent
      })
    } else {
      this.eventHistory = [...this.eventHistory, { ...inspectionEvent, time: context.time }].slice(
        -this.HISTORY_SIZE
      )
    }

    for (let i = 0; i < this.subscribeListeners.length; i++) {
      this.subscribeListeners[i](event, context)
    }
  }

  private _appendTimeline = (record: TimelineRecord) => {
    if (!this.enableTimeline) {
      return
    }

    this.timelineHistory = [...this.timelineHistory, record].slice(-this.TIMELINE_SIZE)
    this.onTimelineRecord?.(record)
    for (const listener of this.timelineListeners) {
      listener(record)
    }
  }

  private _snapshotSpan = (
    span: NativeSpanLike | null,
    defaultName: string
  ): TimelineSpanSnapshot => {
    if (!span) {
      return {
        name: defaultName,
        attributes: {},
        events: [],
        eventsTruncated: false
      }
    }

    const rawEvents = span.events ?? []
    const events = rawEvents
      .slice(-this.TIMELINE_EVENT_LIMIT)
      .map(([name, startTime, attributes]) => ({
        name,
        time: this._bigIntToIsoString(startTime),
        attributes
      }))

    return {
      name: span.name,
      spanId: span.spanId,
      traceId: span.traceId,
      attributes: Object.fromEntries(span.attributes.entries()),
      events,
      eventsTruncated: rawEvents.length > this.TIMELINE_EVENT_LIMIT
    }
  }

  private _bigIntToIsoString = (value: bigint) => {
    const millis = Number(value / BigInt(1_000_000))
    return Number.isFinite(millis) ? new Date(millis).toISOString() : this._nowIso()
  }

  private _nowIso = () => new Date(this.execution.clock.currentTimeMillisUnsafe()).toISOString()

  private _nextTimelineRecordId = () => `t${++this.timelineRecordSeq}`

  private _nextDispatchId = () => `d${++this.dispatchSeq}`

  private _nextEffectId = () => `e${++this.effectSeq}`

  subscribe = (listener: DispatchEvent<any, StoreEvent>) => {
    this.subscribeListeners.push(listener)

    return () => {
      this.subscribeListeners.splice(this.subscribeListeners.indexOf(listener), 1)
    }
  }

  /**
   * React hook to get a derived state based on the state of the global store
   */
  useSelect = <T>(sel: (s: AppState<P>) => T): T => {
    return this.useAppStore(sel)
  }

  isProcessRunning = (processName: ProcessName) => {
    return this.processes[processName] != undefined
  }

  _debugGetProcesses = () => {
    return this.processes
  }

  /**
   * Dispose every process and interrupt active Tasks and in-flight Commands.
   *
   * Revocation is immediate. The Effect runtime is disposed after the Task and
   * Command stops settle, so their resource finalizers run before teardown.
   * Run the returned Effect when the caller needs every finalizer to finish.
   */
  disposeAll = (): Effect.Effect<void> =>
    Effect.suspend(() => {
      if (this.disposal?.status == 'complete') {
        return replayExit(this.disposal.exit)
      }
      if (this.disposal?.status == 'running') {
        return Deferred.await(this.disposal.completed)
      }

      // `Effect.suspend` evaluates this claim atomically before yielding, so
      // concurrent callers share one completion signal and one teardown.
      const completed = Deferred.makeUnsafe<void>()
      this.disposal = { status: 'running', completed }

      return Effect.uninterruptible(
        Effect.exit(this._disposeAll()).pipe(
          Effect.tap((exit) =>
            Effect.sync(() => {
              this.disposal = { status: 'complete', exit }
            }).pipe(Effect.andThen(Deferred.done(completed, exit)))
          ),
          Effect.flatMap(replayExit)
        )
      )
    })

  /** Scope shutdown should not replay an already-observed disposal failure. */
  finalizeScope = (): Effect.Effect<void> =>
    Effect.suspend(() =>
      this.disposal?.status == 'complete' ? Effect.void : this.disposeAll().pipe(Effect.orDie)
    )

  private _disposeAll = (): Effect.Effect<void> => {
    const store = this
    return Effect.gen(function* () {
      store.disposing = true

      const stoppingCommands = [...store.activeUpdateEffects].map(
        (updateEffect) => updateEffect.stop
      )
      store.activeUpdateEffects.clear()

      const stoppingTasks: Effect.Effect<void>[] = []
      let failure: Cause.Cause<never> | undefined
      const processNames = Object.keys(store.processes)
      for (const processName of processNames) {
        const exit = yield* Effect.exit(
          Effect.sync(() => {
            const process = store.processes[processName]
            if (process) {
              stoppingTasks.push(...store._stopAllTasks(process))
            }
            store.disposeProcess(processName)
          })
        )
        if (Exit.isFailure(exit)) {
          failure = failure ? Cause.combine(failure, exit.cause) : exit.cause
        }
      }

      store.subscribeListeners = []
      const stopsExit = yield* Effect.exit(
        Effect.all([...stoppingCommands, ...stoppingTasks], {
          concurrency: 'unbounded',
          discard: true
        })
      )
      if (Exit.isFailure(stopsExit)) {
        failure = failure ? Cause.combine(failure, stopsExit.cause) : stopsExit.cause
      }
      if (failure) {
        return yield* Effect.failCause(failure)
      }
    }).pipe(Effect.ensuring(FiberSet.clear(store.execution.fibers)))
  }
}

function replayExit(exit: Exit.Exit<void>): Effect.Effect<void> {
  return Effect.matchCauseEffectEager(exit, {
    onFailure: Effect.failCause,
    onSuccess: () => Effect.void
  })
}

/**
 * Store messages remain available to reducers in their original form, while
 * diagnostic copies redact conventionally named secret fields. This keeps
 * DevTools and timeline inspection useful without retaining credential values.
 */
export function redactForInspection(value: unknown, fieldName?: string): unknown {
  if (fieldName && SENSITIVE_INSPECTION_FIELD.test(fieldName)) {
    return '<redacted>'
  }

  if (Array.isArray(value)) {
    return value.map((child) => redactForInspection(child))
  }

  if (!isPlainObject(value)) {
    return value
  }

  return Object.fromEntries(
    Object.entries(value).map(([key, child]) => [key, redactForInspection(child, key)])
  )
}

const SENSITIVE_INSPECTION_FIELD = /password|token|secret|sensitive/i

function isPlainObject(value: unknown): value is Record<string, unknown> {
  if (!value || typeof value != 'object') {
    return false
  }

  const prototype = Object.getPrototypeOf(value)
  return prototype == Object.prototype || prototype == null
}

type ProcessMissingExceptionContext = {
  processName: string
  message?: any
}
export class ProcessMissingException extends Error {
  processName: string
  messageDetails: string

  constructor(extra: ProcessMissingExceptionContext) {
    super(
      `Process ${JSON.stringify(extra.processName)} is not running.` +
        ` Declare it during store creation in ReduxStore({ processes: { ... } }).` +
        (extra.message ? ` Tried to send message: ${JSON.stringify(extra.message)}` : '')
    )
    this.name = 'ProcessMissingException'
    this.processName = extra.processName
    this.messageDetails = JSON.stringify(extra.message)

    Object.setPrototypeOf(this, ProcessMissingException.prototype)
  }
}

/**
 * Reduces one process message to its next state and optional Command.
 *
 * `init` and `dispose` are runtime lifecycle messages; ordinary messages pass
 * through the module's pure `update`. Command execution happens only after the
 * caller commits the returned state. An impossible transition is a programmer
 * error and escapes synchronously; this boundary does not turn it into data.
 */
function callMessageHandler<M extends ProcessMessage>(
  registeredProcess: Process,
  message: M,
  curProcessState: unknown
): {
  nextProcessState: unknown
  updateEffect: TaskProgram<EffectOutput<ProcessMessage>, unknown, unknown> | null
} {
  const module = registeredProcess.module as {
    init: (arg: unknown) => UpdateResult<unknown, M, unknown, unknown>
    update: (state: unknown, event: M) => UpdateResult<unknown, M, unknown, unknown>
  }

  switch (message.type) {
    case 'init': {
      const { nextProcessState, updateEffect } = normalizeUpdateResult(
        module.init(registeredProcess.initArg)
      )
      return { nextProcessState, updateEffect }
    }
    case 'dispose':
      return { nextProcessState: undefined, updateEffect: null }
    default: {
      const { nextProcessState, updateEffect } = normalizeUpdateResult(
        module.update(curProcessState, message)
      )
      return { nextProcessState, updateEffect }
    }
  }
}

function normalizeUpdateResult<M extends ProcessMessage>(
  msg: UpdateResult<unknown, M, unknown, unknown>
): {
  nextProcessState: unknown
  updateEffect: TaskProgram<EffectOutput<ProcessMessage>, unknown, unknown> | null
} {
  if (Array.isArray(msg)) {
    const [reply, updateEffect] = msg
    return {
      nextProcessState: reply,
      updateEffect:
        (updateEffect as TaskProgram<EffectOutput<ProcessMessage>, unknown, unknown>) ?? null
    }
  }

  return { nextProcessState: msg, updateEffect: null }
}
